/**
 * Agentic v2 ops API client — admin actions for Mission Control Ops.
 *
 * Wraps `/api/v1/admin/agentic-v2/*` endpoints used by the Mission
 * Control Ops dashboard to requeue stuck or failed tasks.
 */

import { apiRequest } from "../client";

const BASE = "/admin/agentic-v2";

/** Default Mission Control Ops search (blockers, gate reason, summaries). */
export const DEFAULT_OPS_ERROR_SEARCH_QUERY = "error";

export type RequeueMode = "failed" | "stuck_queued";

/** Mission Control Ops queue tab (includes text search). */
export type OpsQueueMode = RequeueMode | "search";

export interface OpsQueueTaskItem extends RequeuedTaskItem {
  match_in?: string;
  match_snippet?: string;
  requeue_mode?: RequeueMode | null;
  continue_eligible?: boolean;
}

export interface OpsTaskSearchRequest {
  client_id?: string;
  query: string;
  limit?: number;
}

export interface OpsTaskSearchResponse {
  client_id: string;
  query: string;
  matched: number;
  tasks: OpsQueueTaskItem[];
}

export interface RequeueRequest {
  client_id?: string;
  dry_run?: boolean;
  min_age_seconds?: number;
}

export interface RequeuedTaskItem {
  task_id: string;
  name: string;
  status: string;
  catalogue_key: string | null;
}

export interface RequeueResponse {
  mode: RequeueMode;
  client_id: string;
  dry_run: boolean;
  matched: number;
  requeued: number;
  tasks: RequeuedTaskItem[];
}

export async function requeueFailedTasks(
  body: RequeueRequest = {}
): Promise<RequeueResponse> {
  return apiRequest<RequeueResponse>({
    method: "POST",
    url: `${BASE}/requeue-failed`,
    data: body,
  });
}

export async function requeueStuckQueuedTasks(
  body: RequeueRequest = {}
): Promise<RequeueResponse> {
  return apiRequest<RequeueResponse>({
    method: "POST",
    url: `${BASE}/requeue-stuck-queued`,
    data: body,
  });
}

export interface RequeueTaskRequest {
  client_id?: string;
  mode: RequeueMode;
  dry_run?: boolean;
  min_age_seconds?: number;
}

export async function requeueTask(
  taskId: string,
  body: RequeueTaskRequest
): Promise<RequeuedTaskItem> {
  return apiRequest<RequeuedTaskItem>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/requeue`,
    data: body,
  });
}

export interface OpsQueueStats {
  client_id: string;
  stuck_queued: number;
  failed: number;
  error_matches: number;
  error_matches_capped: boolean;
  error_query: string;
}

export async function getOpsQueueStats(params: {
  client_id?: string;
  error_query?: string;
}): Promise<OpsQueueStats> {
  const search = new URLSearchParams();
  if (params.client_id) search.set("client_id", params.client_id);
  if (params.error_query) search.set("error_query", params.error_query);
  const qs = search.toString();
  return apiRequest<OpsQueueStats>({
    method: "GET",
    url: `${BASE}/ops-queue-stats${qs ? `?${qs}` : ""}`,
  });
}

export async function searchOpsTasks(
  body: OpsTaskSearchRequest
): Promise<OpsTaskSearchResponse> {
  return apiRequest<OpsTaskSearchResponse>({
    method: "POST",
    url: `${BASE}/tasks/search`,
    data: body,
  });
}

export async function previewRequeueQueue(
  mode: RequeueMode,
  body: Omit<RequeueRequest, "dry_run"> = {}
): Promise<RequeueResponse> {
  if (mode === "failed") {
    return requeueFailedTasks({ ...body, dry_run: true });
  }
  return requeueStuckQueuedTasks({ ...body, dry_run: true });
}

export interface BulkContinueRequest {
  client_id?: string;
  task_ids: string[];
  dry_run?: boolean;
}

export interface BulkContinueResponse {
  client_id: string;
  dry_run: boolean;
  matched: number;
  continued: number;
  skipped: number;
  tasks: RequeuedTaskItem[];
}

export async function bulkContinueTasks(
  body: BulkContinueRequest
): Promise<BulkContinueResponse> {
  return apiRequest<BulkContinueResponse>({
    method: "POST",
    url: `${BASE}/tasks/bulk-continue`,
    data: body,
  });
}

export async function continueTask(
  taskId: string,
  body: { client_id?: string; dry_run?: boolean } = {}
): Promise<RequeuedTaskItem> {
  return apiRequest<RequeuedTaskItem>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/continue`,
    data: body,
  });
}

export async function requeueAllInQueue(
  mode: RequeueMode,
  body: Omit<RequeueRequest, "dry_run"> = {}
): Promise<RequeueResponse> {
  if (mode === "failed") {
    return requeueFailedTasks({ ...body, dry_run: false });
  }
  return requeueStuckQueuedTasks({ ...body, dry_run: false });
}

export interface DeleteTaskCounts {
  tasks: number;
  interactions: number;
  messages: number;
  events: number;
  artifacts: number;
  artifact_documents: number;
  artifact_comments: number;
  crm_change_requests_unlinked: number;
  client_memories_unlinked: number;
}

export interface DeleteTaskPreview {
  task_id: string;
  task_name: string;
  task_status: string;
  child_task_ids: string[];
  counts: DeleteTaskCounts;
  blockers: string[];
  dry_run: boolean;
}

export interface DeleteTaskRequest {
  cascade_children?: boolean;
  force?: boolean;
}

export interface InteractionDiagnosticItem {
  interaction_id: string;
  sequence_number: number;
  status: string;
  trigger_type: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  token_usage_total: number;
  cost_usd: number;
  step_count: number;
  highlight_steps: Record<string, unknown>[];
}

export interface TaskDiagnostics {
  task_id: string;
  task_name: string;
  task_status: string;
  client_id: string;
  catalogue_key: string | null;
  gate_blocked_reason: Record<string, unknown> | null;
  gate_blocked_display: Record<string, unknown> | null;
  blockers: string[];
  latest_summary: string | null;
  disposition_reasoning: string | null;
  queued_at: string | null;
  updated_at: string | null;
  interactions: InteractionDiagnosticItem[];
}

export async function getTaskDiagnostics(taskId: string): Promise<TaskDiagnostics> {
  return apiRequest<TaskDiagnostics>({
    method: "GET",
    url: `${BASE}/tasks/${taskId}/diagnostics`,
  });
}

export async function getDeleteTaskPreview(
  taskId: string,
  params: { cascade_children?: boolean; force?: boolean } = {}
): Promise<DeleteTaskPreview> {
  const search = new URLSearchParams();
  if (params.cascade_children) search.set("cascade_children", "true");
  if (params.force) search.set("force", "true");
  const qs = search.toString();
  return apiRequest<DeleteTaskPreview>({
    method: "GET",
    url: `${BASE}/tasks/${taskId}/delete-preview${qs ? `?${qs}` : ""}`,
  });
}

export async function deleteTask(
  taskId: string,
  body: DeleteTaskRequest = {}
): Promise<DeleteTaskPreview> {
  return apiRequest<DeleteTaskPreview>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/delete`,
    data: body,
  });
}

export interface MissionOpsClientBody {
  client_id?: string;
}

export interface MissionStageOpsResponse {
  mission_id: string;
  stage_name?: string | null;
  current_stage?: string | null;
  status?: string | null;
  open_task_count?: number | null;
}

export interface CompleteMissionStageBody extends MissionOpsClientBody {
  force?: boolean;
  reason?: string;
  spawn_followups_from_task_id?: string;
}

export interface ReopenMissionStageBody extends MissionOpsClientBody {
  reason?: string;
}

export interface CompleteMissionBody extends MissionOpsClientBody {
  force?: boolean;
  reason?: string;
}

export interface SetTaskStatusBody extends MissionOpsClientBody {
  status: string;
  reason?: string;
  spawn_followups?: boolean;
}

export async function completeMissionStage(
  missionId: string,
  stageName: string,
  body: CompleteMissionStageBody = {},
): Promise<MissionStageOpsResponse> {
  return apiRequest<MissionStageOpsResponse>({
    method: "POST",
    url: `${BASE}/missions/${missionId}/stages/${encodeURIComponent(stageName)}/complete`,
    data: body,
  });
}

export async function reopenMissionStage(
  missionId: string,
  stageName: string,
  body: ReopenMissionStageBody = {},
): Promise<MissionStageOpsResponse> {
  return apiRequest<MissionStageOpsResponse>({
    method: "POST",
    url: `${BASE}/missions/${missionId}/stages/${encodeURIComponent(stageName)}/reopen`,
    data: body,
  });
}

export async function completeMission(
  missionId: string,
  body: CompleteMissionBody = {},
): Promise<MissionStageOpsResponse> {
  return apiRequest<MissionStageOpsResponse>({
    method: "POST",
    url: `${BASE}/missions/${missionId}/complete`,
    data: body,
  });
}

export async function setTaskStatus(
  taskId: string,
  body: SetTaskStatusBody,
): Promise<Record<string, unknown>> {
  return apiRequest<Record<string, unknown>>({
    method: "POST",
    url: `${BASE}/tasks/${taskId}/set-status`,
    data: body,
  });
}

export const MISSION_OPS_TASK_STATUSES = [
  "queued",
  "gated",
  "working",
  "needs_review",
  "blocked",
  "done",
  "auto_closed",
] as const;

export type MissionOpsTaskStatus = (typeof MISSION_OPS_TASK_STATUSES)[number];
